import TextArea from "../../../Components/Common/TextArea/TextArea";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./ManageUser.scss";
import ErrorMessage, {
  limitMsg,
  minMsg,
} from "../../../Components/Common/ErrorMessage/ErrorMessage";
import {
  CHAR_LIMIT,
  CHAR_MIN,
  NAME_REGEX,
  EMAIL_REGEX,
} from "../../../utils/Common/Constant";
import Button from "../../../Components/Common/Button/Button";
import Input from "../../../Components/Common/Input/Input";
import useDebounce from "../../../hooks/useDebounce";
import useCreateUser from "../../../hooks/useCreateUser";
import useUpdateUser from "../../../hooks/useUpdateUser";
import useDeactivateUser from "../../../hooks/useDeactivateUser";
import useResendInvite from "../../../hooks/useResendInvite";
import useGetUsers from "../../../hooks/useGetUsers";
import useGetProjects from "../../../hooks/useGetProjects";
import useGetBanks from "../../../hooks/useGetBanks";
import useGetBranches from "../../../hooks/useGetBranches";
import { getCompanies } from "../../../services/companyService";
import { exportUsers } from "../../../services/userService";
import SearchableSelect from "../../../Components/Common/SearchableSelect/SearchableSelect";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import UserRowSkeleton from "../../../Components/Common/Skeleton/UserRowSkeleton";
import StepTick from "Assets/Img/StepTick";
import NetworkError from "Components/Common/NetworkError/NetworkError";
import { useToast } from "Components/Common/Toast/ToastContext";
import DownloadIcon from "Assets/Img/DownloadIcon";
import SortIcon from "Assets/Img/SortIcon";
import Table, { Column } from "Components/Table/Table";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyInspectorIcon from "Assets/Img/EmptyInspectorIcon";
import PhoneInput from "../../../Components/Common/PhoneInput/PhoneInput";
import Select from "../../../Components/Common/Select/Select";

// ─── Types ────────────────────────────────────────────────────────────────────

type Role = "Admin" | "Inspector" | "Owner" | "Lender" | "GC" | "Architect";
type UserStatus = "Active" | "Pending" | "Inactive";
type DrawerMode = "new" | "edit";
type StatusClass = "active" | "pending" | "inactive";
type FormErrorKey =
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "address"
  | "company"
  | "bio"
  | "role"
  | "designation"
  | "bankId"
  | "branchId";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface User {
  avatar: string | null;
  lastLoginAt: string | null;
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  company: string;
  bio: string;
  role: Role;
  roles: Role[];
  inspectorMeta?: {
    designation?: string;
    focused?: string[];
  };
  bank?: string;
  bankId?: string;
  bankName?: string;
  branchId?: string;
  status: UserStatus;
  projects: string[];
  initials: string;
  color: string;
}

interface CreatedUserPayload {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  bio?: string;
  address?: string;
}

// Set by Add New Project ("Create new owner/GC/architect"): open the Add User drawer
// with that role and return to the project form afterwards.
interface CreateUserForState {
  createUserFor?: {
    role: Role;
    returnTo: string;
    prefill?: { bankId: string; bankName: string; branchId: string };
  };
}

interface Project {
  id: string;
  name: string;
  bank: string;
  type: string;
}

interface PaginationData {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
}

interface ApiStats {
  total: number;
  byRole: Record<string, number>;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  bio: string;
  roles: string[];
  designation: string;
  focused: string[];
  bankId: string;
  bankName: string;
  branchId: string;
  status: string;
  projects: string[];
}

type FormErrors = Partial<Record<FormErrorKey, string>>;
type TouchedFields = Partial<Record<FormErrorKey, boolean>>;

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES: Role[] = [
  "Admin",
  "Inspector",
  "Owner",
  "Lender",
  "GC",
  "Architect",
];

const ROLE_API_KEY: Record<Role, string> = {
  Admin: "admin",
  Inspector: "inspector",
  Owner: "owner",
  Lender: "lender",
  GC: "gc",
  Architect: "architect",
};

const PROJECT_ROLES: Role[] = ["Owner", "Architect"];

const ROLE_COLORS: Record<Role, string> = {
  Admin: "purple",
  Inspector: "b",
  Owner: "green",
  Lender: "amber",
  GC: "teal",
  Architect: "red",
};

const ROLE_LABELS: Record<Role, string> = {
  Admin: "CSC Admin",
  Inspector: "Inspector",
  Owner: "Owner / Borrower",
  Lender: "Lender / Bank",
  GC: "General Contractor",
  Architect: "Architect",
};

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "pending_invite", label: "Pending invite" },
  { value: "inactive", label: "Inactive" },
];

const DESIGNATION_OPTIONS = [
  { value: "", label: "Select designation…" },
  { value: "senior", label: "Senior" },
  { value: "junior", label: "Junior" },
];

const ACCOUNT_STATUS_OPTIONS = [
  // { value: "Pending", label: "Pending invite" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const EMPTY_FORM: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "+1",
  address: "",
  company: "",
  bio: "",
  roles: [],
  designation: "",
  focused: [],
  bankId: "",
  bankName: "",
  branchId: "",
  status: "Pending",
  projects: [],
};

// ─── Helper Functions ─────────────────────────────────────────────────────────

function getStatusClass(status: string): StatusClass {
  if (status === "Active") return "active";
  if (status === "Pending") return "pending";
  return "inactive";
}

function getProjectPills(projectIds: string[], projects: Project[]): string[] {
  return projectIds
    .slice(0, 2)
    .map((pid) => {
      const project = projects.find((item) => item.id === pid);
      return project ? project.name.split("–")[0].trim() : null;
    })
    .filter((name): name is string => name !== null);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ManageUser(): React.ReactElement {
  const { projects } = useGetProjects() as { projects: Project[] };
  const bank = useGetBanks();
  const [formState, setFormState] = useState<FormState>(EMPTY_FORM);
  const branch = useGetBranches(formState.bankId);
  const { handleCreateUser, loading: createLoading } =
    useCreateUser() as unknown as {
      handleCreateUser: (formState: FormState) => Promise<{
        success: boolean;
        user: CreatedUserPayload;
        error?: string;
      }>;
      loading: boolean;
    };

  const { handleUpdateUser, loading: updateLoading } = useUpdateUser() as {
    handleUpdateUser: (
      id: string,
      formState: FormState,
    ) => Promise<{ success: boolean; error?: string }>;
    loading: boolean;
  };

  const { handleDeactivateUser, loading: deactivateLoading } =
    useDeactivateUser() as {
      handleDeactivateUser: (
        id: string,
      ) => Promise<{ success: boolean; message: string }>;
      loading: boolean;
    };

  const {
    handleResendInvite,
    loading: resendLoading,
    countdown: resendCountdown,
  } = useResendInvite() as {
    handleResendInvite: (
      id: string,
    ) => Promise<{ success: boolean; message: string }>;
    loading: boolean;
    countdown: number;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const createFor = (routeLocation.state as CreateUserForState | null)
    ?.createUserFor;
  const [page, setPage] = useState<number>(1);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<string>("all");
  const [currentSearch, setCurrentSearch] = useState<string>("");
  const debouncedSearch = useDebounce(currentSearch, 500);
  const [currentStatus, setCurrentStatus] = useState<string>("all");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const showToast = useToast();
  const filterParam =
    currentFilter === "all"
      ? ""
      : (ROLE_API_KEY[currentFilter as Role] ?? currentFilter.toLowerCase());

  const {
    users,
    pagination,
    stats: apiStats,
    loading: usersLoading,
    error: usersError,
    refetch,
  } = useGetUsers(
    page,
    10,
    filterParam,
    currentStatus,
    debouncedSearch,
    // currentSort,
  ) as unknown as {
    users: User[];
    pagination: PaginationData;
    stats: ApiStats | null;
    loading: boolean;
    error: string;
    refetch: () => Promise<void>;
  };

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("new");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirmUser, setConfirmUser] = useState<User | null>(null);
  const [toastText, setToastText] = useState<string>("");
  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});

  const firstNameRef = useRef<HTMLDivElement>(null);
  const lastNameRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);
  const roleRef = useRef<HTMLDivElement>(null);
  const designationRef = useRef<HTMLDivElement>(null);
  const bankRef = useRef<HTMLDivElement>(null);
  const branchRef = useRef<HTMLDivElement>(null);
  const drawerBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (drawerOpen && drawerBodyRef.current) {
      drawerBodyRef.current.scrollTop = 0;
    }
  }, [drawerOpen]);

  useEffect(() => {
    if (
      !drawerOpen ||
      !formState.roles.includes("Lender") ||
      !formState.bankName ||
      formState.bankId
    )
      return;
    (
      getCompanies({ q: formState.bankName, page: 1, limit: 5 }) as Promise<{
        data: { data?: { id: string; name: string }[] } | null;
        error: unknown;
      }>
    ).then(({ data }) => {
      const match = data?.data?.find(
        (c) => c.name.toLowerCase() === formState.bankName.toLowerCase(),
      );
      if (match) setFormState((prev) => ({ ...prev, bankId: match.id }));
    });
  }, [drawerOpen, formState.roles, formState.bankName, formState.bankId]);

  useEffect(() => {
    if (!toastVisible) return undefined;
    const timer = window.setTimeout(() => setToastVisible(false), 2800);
    return () => window.clearTimeout(timer);
  }, [toastVisible]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    setPage(1);
  }, [currentFilter, debouncedSearch, currentStatus]);

  const pendingEmailOpen = useRef<string | null>(null);

  useEffect(() => {
    const email = searchParams.get("email");
    if (!email) return;
    pendingEmailOpen.current = email;
    setCurrentSearch(email);
    setSearchParams(
      (prev) => {
        prev.delete("email");
        return prev;
      },
      { replace: true },
    );
  }, []);

  useEffect(() => {
    if (!pendingEmailOpen.current || usersLoading || !users.length) return;
    const found = users.find((u) => u.email === pendingEmailOpen.current);
    if (!found) return;
    pendingEmailOpen.current = null;
    openDrawer("edit", found.id);
  }, [users, usersLoading]);

  useEffect(() => {
    const targetId = searchParams.get("userId");
    if (!targetId || usersLoading || !users.length) return;
    const found = users.find((u) => u.id === targetId);
    if (!found) return;
    openDrawer("edit", targetId);
    setSearchParams(
      (prev) => {
        prev.delete("userId");
        return prev;
      },
      { replace: true },
    );
  }, [users, usersLoading, searchParams]);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);
  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return sortDir === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }),
    [users, sortDir],
  );

  const counts = useMemo<Record<string, number>>(() => {
    const byRole = apiStats?.byRole ?? {};
    const result: Record<string, number> = { all: apiStats?.total ?? 0 };
    ROLES.forEach((role) => {
      result[role] = byRole[ROLE_API_KEY[role]] ?? 0;
    });
    return result;
  }, [apiStats]);

  const validate = (state: FormState): FormErrors => {
    const errors: FormErrors = {};
    if (!state.firstName.trim()) errors.firstName = "First name is required";
    else if (state.firstName.length > CHAR_LIMIT.NAME)
      errors.firstName = limitMsg(CHAR_LIMIT.NAME);
    else if (!NAME_REGEX.test(state.firstName.trim()))
      errors.firstName =
        "First name must not contain numbers or special characters";
    if (!state.lastName.trim()) errors.lastName = "Last name is required";
    else if (state.lastName.length > CHAR_LIMIT.NAME)
      errors.lastName = limitMsg(CHAR_LIMIT.NAME);
    else if (!NAME_REGEX.test(state.lastName.trim()))
      errors.lastName =
        "Last name must not contain numbers or special characters";
    if (!state.email.trim()) {
      errors.email = "Email address is required";
    } else if (state.email.length > CHAR_LIMIT.EMAIL) {
      errors.email = limitMsg(CHAR_LIMIT.EMAIL);
    } else if (!EMAIL_REGEX.test(state.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    if (state.phone.trim()) {
      const phoneDigits = state.phone.replace(/\D/g, "");
      if (phoneDigits.length > 3) {
        if (state.phone.length < CHAR_MIN.CONTACT)
          errors.phone = minMsg(CHAR_MIN.CONTACT);
        else if (state.phone.length > CHAR_LIMIT.PHONE)
          errors.phone = limitMsg(CHAR_LIMIT.PHONE);
      }
    }
    if (state.address.length > CHAR_LIMIT.ADDRESS)
      errors.address = limitMsg(CHAR_LIMIT.ADDRESS);
    if (!state.company.trim())
      errors.company = "Company / Organization is required";
    else if (state.company.length > CHAR_LIMIT.NAME)
      errors.company = limitMsg(CHAR_LIMIT.NAME);
    if (state.bio.length > CHAR_LIMIT.DESCRIPTION)
      errors.bio = limitMsg(CHAR_LIMIT.DESCRIPTION);
    if (!state.roles.length) errors.role = "Please select at least one role";
    if (state.roles.includes("Inspector") && !state.designation) {
      errors.designation = "Please select a designation";
    }
    if (state.roles.includes("Lender")) {
      if (!state.bankId) errors.bankId = "Bank is required";
      if (!state.branchId) errors.branchId = "Branch is required";
    }
    return errors;
  };

  const scrollToFirstError = (errors: FormErrors): void => {
    const order: FormErrorKey[] = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "company",
      "role",
      "designation",
      "bankId",
      "branchId",
    ];
    const refMap: Partial<
      Record<FormErrorKey, React.RefObject<HTMLDivElement | null>>
    > = {
      firstName: firstNameRef,
      lastName: lastNameRef,
      email: emailRef,
      phone: phoneRef,
      company: companyRef,
      role: roleRef,
      designation: designationRef,
      bankId: bankRef,
      branchId: branchRef,
    };
    for (const key of order) {
      if (errors[key] && refMap[key]?.current) {
        refMap[key]!.current!.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        refMap[key]!.current!.querySelector<HTMLElement>(
          "input, select",
        )?.focus();
        break;
      }
    }
  };

  const handleBlur = (field: FormErrorKey): void => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFormErrors(validate(formState));
  };

  const showBankSection = formState.roles.includes("Lender");
  // Project assignment is only allowed when every selected role is Owner/Architect (backend rule).
  const showProjectSection =
    formState.roles.length > 0 &&
    formState.roles.every((r) => PROJECT_ROLES.includes(r as Role));
  const isPendingEdit = drawerMode === "edit" && formState.status === "Pending";
  const drawerUser: User | null =
    editingId != null
      ? (users.find((user) => user.id === editingId) ?? null)
      : null;
  // Only the projects this user was assigned when the drawer opened (edit mode only).
  const assignedProjects = projects.filter((project) =>
    drawerUser?.projects.map(String).includes(String(project.id)),
  );

  const openDrawer = (mode: DrawerMode, userId: string | null = null): void => {
    setDrawerMode(mode);
    setDrawerOpen(true);
    setConfirmOpen(false);
    setFormErrors({});
    setTouched({});
    if (mode === "edit" && userId != null) {
      const user = users.find((item) => item.id === userId);
      if (!user) return;
      setEditingId(userId);
      setFormState({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone || "+1",
        address: user.address ?? "",
        company: user.company,
        bio: user.bio,
        roles: [...user.roles],
        designation: user.inspectorMeta?.designation ?? "",
        focused: user.inspectorMeta?.focused ?? [],
        bankId: user.bankId ?? "",
        bankName: user.bankName ?? "",
        branchId: user.branchId ?? "",
        status: user.status,
        projects: [...user.projects],
      });
    } else {
      setEditingId(null);
      setFormState(EMPTY_FORM);
    }
  };
  const closeDrawer = (): void => {
    setDrawerOpen(false);
    setEditingId(null);
    setConfirmOpen(false);
    // Cancelled while creating a contact for a project: go back to the project form.
    if (createFor && drawerMode === "new") {
      navigate(createFor.returnTo, { replace: true });
    }
  };

  useEffect(() => {
    if (!createFor) return;
    openDrawer("new");
    setFormState({
      ...EMPTY_FORM,
      roles: [createFor.role],
      ...createFor.prefill,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (
    field: keyof FormState,
    value: string | string[],
  ): void => {
    const sanitized =
      field === "phone" && typeof value === "string"
        ? value.replace(/[^0-9+]/g, "")
        : value;
    const next = { ...formState, [field]: sanitized };
    setFormState(next);
    if (touched[field as FormErrorKey]) setFormErrors(validate(next));
  };

  const handleRoleSelect = (role: Role): void => {
    // Multi-select toggle in both create and edit (roles can be assigned or unassigned).
    const roles = formState.roles.includes(role)
      ? formState.roles.filter((r) => r !== role)
      : [...formState.roles, role];
    const next: FormState = {
      ...formState,
      roles,
      designation: roles.includes("Inspector") ? formState.designation : "",
      focused: roles.includes("Inspector") ? formState.focused : [],
      bankId: roles.includes("Lender") ? formState.bankId : "",
      bankName: roles.includes("Lender") ? formState.bankName : "",
      branchId: roles.includes("Lender") ? formState.branchId : "",
      projects: roles.every((r) => PROJECT_ROLES.includes(r as Role))
        ? formState.projects
        : [],
    };
    setFormState(next);
    setTouched((prev) => ({ ...prev, role: true }));
    setFormErrors(validate(next));
  };

  const toggleProject = (projectId: string): void => {
    if (!projectId) return;
    const id = String(projectId);
    setFormState((prev) => {
      const current = prev.projects.map(String);
      const selected = current.includes(id)
        ? prev.projects.filter((p) => String(p) !== id)
        : [...prev.projects, id];
      return { ...prev, projects: selected };
    });
  };

  const saveUser = async (): Promise<void> => {
    const allTouched: TouchedFields = {
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      company: true,
      bio: true,
      role: true,
      bankId: true,
      branchId: true,
    };
    setTouched(allTouched);
    const errors = validate(formState);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      scrollToFirstError(errors);
      return;
    }
    setFormErrors({});

    if (editingId) {
      const { success, error: updateError } = await handleUpdateUser(
        editingId,
        formState,
      );
      if (!success) {
        showToast(`⚠ ${updateError || "Failed to update user."}`);
        return;
      }
      showToast(
        `✓ ${formState.firstName} ${formState.lastName} updated successfully`,
      );
      closeDrawer();
      await refetch();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const {
        success,
        user: createdUser,
        error: createError,
      } = await handleCreateUser(formState);
      if (!success) {
        showToast(`⚠ ${createError || "Failed to create user."}`);
        return;
      }
      showToast(`✓ Invite sent to ${createdUser.email}`);
      if (createFor) {
        navigate(createFor.returnTo, {
          replace: true,
          state: {
            createdUser: {
              role: createFor.role.toLowerCase(),
              user: createdUser,
            },
          },
        });
        return;
      }
      closeDrawer();
      await refetch();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const openConfirm = (user: User): void => {
    setConfirmUser(user);
    setConfirmOpen(true);
  };

  const confirmDeactivate = async (): Promise<void> => {
    if (!confirmUser) return;
    const { success, message } = await handleDeactivateUser(confirmUser.id);
    if (!success) {
      showToast(`⚠ ${message}`);
      return;
    }
    showToast(`${confirmUser.firstName} ${confirmUser.lastName} deactivated`);
    setConfirmOpen(false);
    closeDrawer();
    await refetch();
  };

  const resendInvite = async (user: User): Promise<void> => {
    const { success, message } = await handleResendInvite(user.id);
    showToast(success ? message : `⚠ ${message}`);
  };
  // Last login function
  function getLastLoginText(lastLoginAt: string | null): string {
    if (!lastLoginAt) {
      return "Last login: Never";
    }
    const diffDays = Math.floor(
      (new Date().getTime() - new Date(lastLoginAt).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) {
      return "Last login: Today";
    }

    return `Last login: ${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  }
  return (
    <div className="manage-user-page">
      <div className="page">
        <div className="page-header">
          <div>
            <div className="page-title">Manage Users</div>
            <div className="page-sub">
              Add, edit, and assign users across all roles and projects. Only
              CSC Admins can manage users.
            </div>
          </div>
          <Button
            className="btn btn-primary"
            type="button"
            onClick={() => openDrawer("new")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              aria-hidden="true"
            >
              <line
                x1="7"
                y1="1"
                x2="7"
                y2="13"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <line
                x1="1"
                y1="7"
                x2="13"
                y2="7"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Add User
          </Button>
        </div>

        <div className="role-stats">
          <div
            className={`role-chip all ${currentFilter === "all" ? "active" : ""}`}
            onClick={() => setCurrentFilter("all")}
          >
            <span className="rc-label">All Users</span>
            <span className="rc-count">{counts.all}</span>
          </div>
          {ROLES.map((role) => (
            <div
              key={role}
              className={`role-chip ${role.toLowerCase()} ${currentFilter === role ? "active" : ""}`}
              onClick={() => setCurrentFilter(role)}
            >
              <div className="rc-dot" />
              <span className="rc-label">{ROLE_LABELS[role]}</span>
              <span className="rc-count">{counts[role]}</span>
            </div>
          ))}
        </div>

        <div className="toolbar">
          <div className="search-wrap">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle
                cx="6"
                cy="6"
                r="4.5"
                stroke="var(--tx3)"
                strokeWidth="1.3"
              ></circle>
              <line
                x1="9.5"
                y1="9.5"
                x2="13"
                y2="13"
                stroke="var(--tx3)"
                strokeWidth="1.3"
                strokeLinecap="round"
              ></line>
            </svg>
            <Input
              className="search-input"
              type="text"
              name="search"
              autoComplete="off"
              id="search"
              placeholder="Search users by name, email, or company…"
              value={currentSearch}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentSearch(event.target.value)
              }
              label={undefined}
            />
          </div>
          <Select
            className="filter-select"
            options={STATUS_FILTER_OPTIONS}
            value={currentStatus}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              setCurrentStatus(event.target.value)
            }
            height={"36px"}
          />
          <div className="toolbar-right">
            <Button
              className="btn btn-secondary"
              type="button"
              disabled={exportLoading}
              onClick={async () => {
                setExportLoading(true);
                try {
                  await exportUsers({
                    role: filterParam,
                    status: currentStatus !== "all" ? currentStatus : "",
                    q: debouncedSearch,
                  });
                } catch {
                  showToast("Failed to export. Please try again.");
                } finally {
                  setExportLoading(false);
                  showToast("Exported user list");
                }
              }}
            >
              {exportLoading ? (
                <>
                  <span className="btn-spinner" /> Exporting
                </>
              ) : (
                <>
                  <DownloadIcon />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>

        <Table<User>
          data={usersLoading || usersError ? [] : sortedUsers}
          tableClassName="table"
          onRowClick={(user) => openDrawer("edit", user.id)}
          fallbackUI={
            usersLoading ? (
              <>
                {Array.from({ length: 8 }).map((_, i) => (
                  <UserRowSkeleton key={i} />
                ))}
              </>
            ) : usersError ? (
              <div className="empty-state">
                <NetworkError />
              </div>
            ) : (
              <EmptyState
                icon={<EmptyInspectorIcon />}
                title="No users match your current filters."
                noBorder
              />
            )
          }
          columns={
            [
              {
                header: (
                  <>
                    User{" "}
                    <span
                      className="sorted-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      }}
                    >
                      <SortIcon direction={sortDir} />
                    </span>
                  </>
                ),
                className: "sorted",
                render: (user: User) => (
                  <div className="user-info">
                    <div
                      className={`user-avatar user-avatar--${user.role.toLowerCase()}`}
                    >
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.firstName} />
                      ) : (
                        user.initials
                      )}
                    </div>
                    <div>
                      <div className="user-name">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="user-email">{user.email}</div>
                    </div>
                  </div>
                ),
              },
              {
                header: "Role",
                render: (user: User) => (
                  <div className="role-pills">
                    {(user.roles.length ? user.roles : [user.role]).map((r) => (
                      <span className="role-pill" key={r}>
                        <span className="rb-dot" />
                        {ROLE_LABELS[r]}
                      </span>
                    ))}
                  </div>
                ),
              },
              {
                header: "Company / Bank",
                render: (user: User) => (
                  <div className="company-cell">
                    <span>
                      {user.company || user.bank || (
                        <span className="empty-dash">—</span>
                      )}
                    </span>
                  </div>
                ),
              },
              {
                header: "Status",
                className: "",
                render: (user: User) => (
                  <div className="status-col">
                    <div
                      className={`status-dot ${getStatusClass(user.status)}`}
                    />
                    <span
                      className={`status-label ${getStatusClass(user.status)}`}
                    >
                      {user.status}
                    </span>
                  </div>
                ),
              },
              {
                header: "Projects Assigned",
                render: (user: User) => {
                  const pills = getProjectPills(user.projects, projects);
                  return (
                    <div className="project-pills">
                      {user.projects && user.projects.length > 0 ? (
                        <>
                          {pills.map((name) => (
                            <span key={name} className="proj-pill">
                              {name}
                            </span>
                          ))}
                          {user.projects.length > 2 && (
                            <span className="proj-pill more">
                              +{user.projects.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="company-cell">
                          No Project assigned
                        </span>
                      )}
                    </div>
                  );
                },
              },
              {
                header: "",
                render: (user: User) => (
                  <div className="row-actions">
                    <Button
                      className="btn-ghost"
                      type="button"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        openDrawer("edit", user.id);
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                ),
              },
            ] as Column<User>[]
          }
        />

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={(p: number) => setPage(p)}
        />
      </div>

      <div
        className={`drawer-overlay ${drawerOpen ? "open" : ""}`}
        onClick={closeDrawer}
      />
      <div className={`drawer ${drawerOpen ? "open" : ""}`}>
        <div className="drawer-header">
          <div className="drawer-title">
            {drawerMode === "edit" && drawerUser
              ? `Edit User — ${drawerUser.firstName} ${drawerUser.lastName}`
              : "Add New User"}
          </div>
          <Button className="drawer-close" type="button" onClick={closeDrawer}>
            ×
          </Button>
        </div>
        <div className="drawer-body" ref={drawerBodyRef}>
          {drawerMode === "edit" && drawerUser ? (
            <div className="profile-preview">
              <div
                className={`pp-avatar pp-avatar--${drawerUser.role.toLowerCase()}`}
              >
                {drawerUser.avatar ? (
                  <img src={drawerUser.avatar} alt={drawerUser.firstName} />
                ) : (
                  drawerUser.initials
                )}
              </div>
              <div className="pp-info">
                <div className="pp-name">
                  {drawerUser.firstName} {drawerUser.lastName}
                </div>
                <div className="pp-role">
                  {(drawerUser.roles.length
                    ? drawerUser.roles
                    : [drawerUser.role]
                  ).map((r) => (
                    <span className={`role-badge rb-${ROLE_COLORS[r]}`} key={r}>
                      {ROLE_LABELS[r]}
                    </span>
                  ))}
                </div>
              </div>
              <div className="pp-actions">
                <span className="pp-meta">
                  {/* Last login: 2 days ago */}
                  {getLastLoginText(drawerUser.lastLoginAt)}
                </span>
                {drawerUser?.status === "Pending" ? (
                  <Button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    onClick={() => resendInvite(drawerUser)}
                    disabled={resendLoading || resendCountdown > 0}
                  >
                    {resendLoading
                      ? "Sending..."
                      : resendCountdown > 0
                        ? `Resend Invite (${resendCountdown}s)`
                        : "Resend Invite"}
                  </Button>
                ) : (
                  ""
                )}
              </div>
            </div>
          ) : null}

          <div className="form-section">
            <div className="form-section-title">Personal Information</div>
            <div className="field-row cols2">
              <div className="field" ref={firstNameRef}>
                <label>
                  First Name <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  autoComplete="off"
                  name="userName"
                  value={formState.firstName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("firstName", event.target.value)
                  }
                  onBlur={() => handleBlur("firstName")}
                  className={
                    touched.firstName && formErrors.firstName
                      ? "input-error"
                      : ""
                  }
                  placeholder="First name"
                  label={undefined}
                />
                <ErrorMessage
                  value={formState.firstName}
                  have={CHAR_LIMIT.NAME}
                  touched={touched.firstName}
                  error={formErrors.firstName}
                />
              </div>
              <div className="field" ref={lastNameRef}>
                <label>
                  Last Name <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  name="userLastName"
                  value={formState.lastName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("lastName", event.target.value)
                  }
                  onBlur={() => handleBlur("lastName")}
                  className={
                    touched.lastName && formErrors.lastName ? "input-error" : ""
                  }
                  placeholder="Last name"
                  label={undefined}
                />
                <ErrorMessage
                  value={formState.lastName}
                  have={CHAR_LIMIT.NAME}
                  touched={touched.lastName}
                  error={formErrors.lastName}
                />
              </div>
            </div>
            <div className="field-row cols2">
              <div className="field" ref={emailRef}>
                <label>
                  Email Address <span className="req">*</span>
                </label>
                <Input
                  type="email"
                  value={formState.email}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("email", event.target.value)
                  }
                  onBlur={() => handleBlur("email")}
                  className={
                    touched.email && formErrors.email ? "input-error" : ""
                  }
                  placeholder="email@company.com"
                  label={undefined}
                  disabled={drawerMode === "edit"}
                />
                <ErrorMessage
                  value={formState.email}
                  have={CHAR_LIMIT.EMAIL}
                  touched={touched.email}
                  error={formErrors.email}
                />
              </div>
              <div className="field" ref={phoneRef}>
                <label>Phone / Mobile</label>
                <PhoneInput
                  value={formState.phone}
                  onChange={(value) => handleFieldChange("phone", value)}
                  onBlur={() => handleBlur("phone")}
                  error={!!(touched.phone && formErrors.phone)}
                />
                <ErrorMessage
                  value={formState.phone}
                  min={CHAR_MIN.CONTACT}
                  have={CHAR_LIMIT.PHONE}
                  touched={touched.phone}
                  error={formErrors.phone}
                />
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label>Address</label>
                <Input
                  type="text"
                  value={formState.address}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("address", event.target.value)
                  }
                  placeholder="Street address, city, state…"
                  label={undefined}
                />
                <ErrorMessage
                  value={formState.address}
                  have={CHAR_LIMIT.ADDRESS}
                  touched={touched.address}
                  error={formErrors.address}
                />
              </div>
            </div>
            <div className="field-row" ref={companyRef}>
              <div className="field">
                <label>
                  Company / Organization <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  value={formState.company}
                  className={
                    touched.company && formErrors.company ? "input-error" : ""
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("company", event.target.value)
                  }
                  onBlur={() => handleBlur("company")}
                  placeholder="Company name"
                  label={undefined}
                />
                <ErrorMessage
                  value={formState.company}
                  have={CHAR_LIMIT.NAME}
                  touched={touched.company}
                  error={formErrors.company}
                />
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label>Bio / Notes</label>
                <TextArea
                  value={formState.bio}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleFieldChange("bio", event.target.value)
                  }
                  onBlur={() => handleBlur("bio")}
                  placeholder="Brief description, specialties, notes…"
                  maxChars={CHAR_LIMIT.DESCRIPTION}
                />
                <ErrorMessage
                  value={formState.bio}
                  touched={touched.bio}
                  error={formErrors.bio}
                />
              </div>
            </div>
          </div>

          <div className="form-section" ref={roleRef}>
            <div className="form-section-title">Role</div>
            <div className="role-selector">
              {ROLES.map((role) => {
                const color = ROLE_COLORS[role];
                const selected = formState.roles.includes(role);
                return (
                  <Button
                    key={role}
                    type="button"
                    className={`role-option color-${color}${selected ? ` selected sel-${color}` : ""}`}
                    onClick={() => handleRoleSelect(role)}
                  >
                    <div className="role-dot" />
                    <div className="role-name">{ROLE_LABELS[role]}</div>
                  </Button>
                );
              })}
            </div>
            {touched.role && formErrors.role && (
              <span className="field-error mt-2">{formErrors.role}</span>
            )}
          </div>

          {formState.roles.includes("Inspector") && (
            <div className="form-section">
              <div className="form-section-title">Inspector Details</div>
              <div className="field-row" ref={designationRef}>
                <div className="field">
                  <label>
                    Designation <span className="req">*</span>
                  </label>
                  <Select
                    height={"38px"}
                    options={DESIGNATION_OPTIONS}
                    value={formState.designation}
                    className={
                      touched.designation && formErrors.designation
                        ? "input-error"
                        : ""
                    }
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      handleFieldChange("designation", e.target.value)
                    }
                    onBlur={() => handleBlur("designation")}
                  />
                  {touched.designation && formErrors.designation && (
                    <span className="field-error">
                      {formErrors.designation}
                    </span>
                  )}
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Focused</label>
                  <div className="focus-group">
                    {["Residential", "Commercial"].map((opt) => {
                      const val = opt.toLowerCase();
                      const checked = formState.focused.includes(val);
                      return (
                        <Button
                          key={opt}
                          type="button"
                          className={`focus-opt${checked ? " focus-opt--sel" : ""}`}
                          onClick={() => {
                            const next = checked
                              ? formState.focused.filter((v) => v !== val)
                              : [...formState.focused, val];
                            handleFieldChange("focused", next);
                          }}
                        >
                          <span className="focus-check">
                            {checked ? <StepTick /> : ""}
                          </span>
                          {opt}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className={`form-section bank-section${showBankSection ? "" : " hidden"}`}
          >
            <div className="form-section-title">Bank Affiliation</div>
            <div className="field" ref={bankRef}>
              <label>
                Bank / Institution <span className="req">*</span>
              </label>
              <SearchableSelect
                id="bankId"
                value={formState.bankId}
                options={
                  formState.bankId &&
                  formState.bankName &&
                  !bank.options.find((o) => o.id === formState.bankId)
                    ? [
                        { id: formState.bankId, name: formState.bankName },
                        ...bank.options,
                      ]
                    : bank.options
                }
                getOptionValue={(b) => b.id}
                getOptionLabel={(b) => b.name}
                placeholder={bank.loading ? "Loading..." : "Search bank..."}
                error={!!(touched.bankId && formErrors.bankId)}
                apiSearch
                onSearch={bank.onSearch}
                hasMore={bank.hasMore && !bank.loading}
                onLoadMore={bank.onLoadMore}
                onChange={(id, selected) => {
                  const next = {
                    ...formState,
                    bankId: id,
                    bankName: selected?.name ?? "",
                    branchId: "",
                  };
                  setFormState(next);
                  setTouched((prev) => ({ ...prev, bankId: true }));
                  setFormErrors(validate(next));
                }}
              />
              {touched.bankId && formErrors.bankId && (
                <span className="field-error">{formErrors.bankId}</span>
              )}
              <div className={`bank-note${showBankSection ? " visible" : ""}`}>
                Lender users are restricted to viewing only projects affiliated
                with their bank.
              </div>
            </div>
            <div className="field mt-3 branchNameSection" ref={branchRef}>
              <label>
                Branch <span className="req">*</span>
              </label>
              <SearchableSelect
                id="branchId"
                value={formState.branchId}
                options={
                  formState.branchId &&
                  drawerUser?.bank &&
                  !branch.options.find((o) => o.id === formState.branchId)
                    ? [
                        {
                          id: formState.branchId,
                          name: drawerUser.bank,
                          address: "",
                        },
                        ...branch.options,
                      ]
                    : branch.options
                }
                getOptionValue={(b) => b.id}
                getOptionLabel={(b) =>
                  b.name + (b.address ? ` — ${b.address}` : "")
                }
                placeholder={branch.loading ? "Loading..." : "Search branch..."}
                disabled={!formState.bankId || branch.loading}
                error={!!(touched.branchId && formErrors.branchId)}
                apiSearch
                onSearch={branch.onSearch}
                hasMore={branch.hasMore && !branch.loading}
                onLoadMore={branch.onLoadMore}
                onChange={(id) => {
                  const next = { ...formState, branchId: id };
                  setFormState(next);
                  setTouched((prev) => ({ ...prev, branchId: true }));
                  setFormErrors(validate(next));
                }}
              />
              {touched.branchId && formErrors.branchId && (
                <span className="field-error">{formErrors.branchId}</span>
              )}
            </div>
          </div>

          <div className="form-section mb-5 accStatus">
            <div className="form-section-title">Account Status</div>
            <div className="field">
              <label>Status</label>
              <Select
                options={ACCOUNT_STATUS_OPTIONS}
                value={formState.status}
                disabled={drawerMode === "new" || isPendingEdit}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFieldChange("status", event.target.value)
                }
                height={"38px"}
              />
              {isPendingEdit && (
                <span className="field-hint">
                  Pending users cannot have their status changed until they
                  accept the invitation.
                </span>
              )}
            </div>
          </div>
          {drawerMode === "edit" && assignedProjects.length > 0 && (
              <div className="form-section">
                <div className="form-section-title">Project Assignments</div>
                <div className="field-subtitle">
                  {showProjectSection
                    ? "Projects currently assigned to this user. Deselect a project to unassign it."
                    : "Projects currently assigned to this user. Assignments can only be changed for users who are only Owner or Architect."}
                </div>
                <div className="proj-assign-list">
                  {assignedProjects.map((project) => {
                    const assigned = formState.projects
                      .map(String)
                      .includes(String(project.id));
                    return (
                      <Button
                        key={project.id}
                        type="button"
                        className={`proj-assign-item${assigned ? " assigned" : ""}`}
                        disabled={!showProjectSection}
                        onClick={() => toggleProject(project.id)}
                      >
                        <div className="pai-check">{assigned ? "✓" : ""}</div>
                        <div className="pai-info">
                          <div className="pai-name">{project.name}</div>
                          <div className="pai-meta">
                            {project.bank} · {project.type}
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

          {drawerMode === "edit" &&
            drawerUser &&
            !drawerUser.roles.includes("Admin") &&
            drawerUser.status !== "Inactive" && (
              <div className="form-section">
                <div className="form-section-title danger-title">
                  Danger Zone
                </div>
                <div className="danger-zone">
                  <div className="dz-title">Deactivate Account</div>
                  <div className="dz-text">
                    Deactivating this user will revoke their platform access
                    immediately. All project history and records are preserved.
                  </div>
                  <Button
                    className="btn-danger"
                    type="button"
                    onClick={() => openConfirm(drawerUser)}
                    disabled={drawerUser.status?.toLowerCase() === "inactive"}
                  >
                    Deactivate {drawerUser.firstName} {drawerUser.lastName}
                  </Button>
                </div>
              </div>
            )}
        </div>
        <div className="drawer-footer">
          <Button
            className="btn btn-secondary"
            type="button"
            onClick={closeDrawer}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            type="button"
            onClick={saveUser}
            disabled={createLoading || updateLoading}
          >
            {drawerMode === "edit" ? (
              updateLoading ? (
                <>
                  <span className="btn-spinner" /> Saving
                </>
              ) : (
                "Save Changes"
              )
            ) : createLoading ? (
              <>
                <span className="btn-spinner" /> Creating
              </>
            ) : (
              "Create User & Send Invite"
            )}
          </Button>
        </div>
      </div>

      <div className={`confirm-overlay ${confirmOpen ? "open" : ""}`}>
        <div className="confirm-box">
          <div className="confirm-title">
            Deactivate {confirmUser?.firstName} {confirmUser?.lastName}?
          </div>
          <div className="confirm-text">
            {confirmUser
              ? `${confirmUser.firstName} will immediately lose access to the platform. Their project history and records are preserved.`
              : ""}
          </div>
          <div className="confirm-btns">
            <Button
              className="btn btn-secondary"
              type="button"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-danger"
              type="button"
              onClick={confirmDeactivate}
              disabled={deactivateLoading}
            >
              {deactivateLoading ? "Deactivating..." : "Confirm"}
            </Button>
          </div>
        </div>
      </div>

      <div className={`toast${toastVisible ? " show" : ""}`}>{toastText}</div>
    </div>
  );
}
